import type { AchievementDef } from './types';

export const generalAchievements: AchievementDef[] = [
  {
    key: 'general.first_game',
    sport: 'general',
    category: 'activity',
    name: { ru: 'Дебют', en: 'Debut' },
    description: { ru: '1 завершённая игра', en: '1 finished game' },
    icon: 'zap',
    color: '#FF5C35',
    check: (s) => s.gamesPlayed >= 1,
    progress: (s) => ({ current: Math.min(s.gamesPlayed, 1), target: 1 }),
  },
  {
    key: 'general.ten_games',
    sport: 'general',
    category: 'activity',
    name: { ru: 'Десятка', en: 'Tenfold' },
    description: { ru: '10 завершённых игр', en: '10 finished games' },
    icon: 'medal',
    color: '#22C55E',
    check: (s) => s.gamesPlayed >= 10,
    progress: (s) => ({ current: Math.min(s.gamesPlayed, 10), target: 10 }),
  },
  {
    key: 'general.fifty_games',
    sport: 'general',
    category: 'activity',
    name: { ru: 'Полтинник', en: 'Half a hundred' },
    description: { ru: '50 завершённых игр', en: '50 finished games' },
    icon: 'trophy',
    color: '#3B82F6',
    check: (s) => s.gamesPlayed >= 50,
    progress: (s) => ({ current: Math.min(s.gamesPlayed, 50), target: 50 }),
  },
  {
    key: 'general.hundred_games',
    sport: 'general',
    category: 'activity',
    name: { ru: 'Сотник', en: 'Centurion' },
    description: { ru: '100 завершённых игр', en: '100 finished games' },
    icon: 'crown',
    color: '#A855F7',
    check: (s) => s.gamesPlayed >= 100,
    progress: (s) => ({ current: Math.min(s.gamesPlayed, 100), target: 100 }),
  },
  {
    key: 'general.streak_3',
    sport: 'general',
    category: 'special',
    name: { ru: 'Серия 3', en: '3-week streak' },
    description: { ru: '3 недели подряд с играми', en: '3 consecutive weeks with games' },
    icon: 'flame',
    color: '#FF5C35',
    check: (s) => s.currentWeekStreak >= 3,
    progress: (s) => ({ current: Math.min(s.currentWeekStreak, 3), target: 3 }),
  },
  {
    key: 'general.streak_5',
    sport: 'general',
    category: 'special',
    name: { ru: 'Серия 5', en: '5-week streak' },
    description: { ru: '5 недель подряд с играми', en: '5 consecutive weeks with games' },
    icon: 'flame',
    color: '#FF8A00',
    check: (s) => s.currentWeekStreak >= 5,
    progress: (s) => ({ current: Math.min(s.currentWeekStreak, 5), target: 5 }),
  },
  {
    key: 'general.streak_10',
    sport: 'general',
    category: 'special',
    name: { ru: 'Месячная привычка', en: 'Monthly habit' },
    description: { ru: '10 недель подряд с играми', en: '10 consecutive weeks with games' },
    icon: 'flame',
    color: '#EF4444',
    check: (s) => s.currentWeekStreak >= 10,
    progress: (s) => ({ current: Math.min(s.currentWeekStreak, 10), target: 10 }),
  },
  {
    key: 'general.iron',
    sport: 'general',
    category: 'special',
    name: { ru: 'Железный', en: 'Iron-clad' },
    description: { ru: 'Посещаемость ≥95% при 10+ играх', en: '95%+ attendance over 10+ games' },
    icon: 'shield',
    color: '#06B6D4',
    check: (s) => s.attendance >= 95 && s.gamesPlayed >= 10,
    // Compound criterion (attendance ≥95% AND games ≥10) — show progress
    // toward the LAGGING requirement so a brand-new user (attendance defaults
    // to 100% with zero games) doesn't see a misleading "95/95" full bar.
    progress: (s) => s.gamesPlayed < 10
      ? { current: s.gamesPlayed, target: 10 }
      : { current: Math.min(s.attendance, 95), target: 95 },
  },
  {
    key: 'general.first_win',
    sport: 'general',
    category: 'wins',
    name: { ru: 'Первая победа', en: 'First win' },
    description: { ru: '1 победа', en: '1 win' },
    icon: 'trophy',
    color: '#22C55E',
    check: (s) => s.wins >= 1,
    progress: (s) => ({ current: Math.min(s.wins, 1), target: 1 }),
  },
  {
    key: 'general.ten_wins',
    sport: 'general',
    category: 'wins',
    name: { ru: '10 побед', en: '10 wins' },
    description: { ru: '10 побед', en: '10 wins' },
    icon: 'trophy',
    color: '#22C55E',
    check: (s) => s.wins >= 10,
    progress: (s) => ({ current: Math.min(s.wins, 10), target: 10 }),
  },
  {
    key: 'general.fifty_wins',
    sport: 'general',
    category: 'wins',
    name: { ru: '50 побед', en: '50 wins' },
    description: { ru: '50 побед', en: '50 wins' },
    icon: 'trophy',
    color: '#FBBF24',
    check: (s) => s.wins >= 50,
    progress: (s) => ({ current: Math.min(s.wins, 50), target: 50 }),
  },
  {
    key: 'general.win_streak_3',
    sport: 'general',
    category: 'wins',
    name: { ru: 'Хет-трик', en: 'Hat-trick' },
    description: { ru: '3 победы подряд', en: '3 consecutive wins' },
    icon: 'flame',
    color: '#F472B6',
    check: (s) => s.bestWinStreak >= 3,
    progress: (s) => ({ current: Math.min(s.bestWinStreak, 3), target: 3 }),
  },
  {
    key: 'general.first_mvp',
    sport: 'general',
    category: 'mvp',
    name: { ru: 'Первый MVP', en: 'First MVP' },
    description: { ru: '1 раз MVP игры', en: 'First time MVP' },
    icon: 'crown',
    color: '#FBBF24',
    check: (s) => s.mvpCount >= 1,
    progress: (s) => ({ current: Math.min(s.mvpCount, 1), target: 1 }),
  },
  {
    key: 'general.ten_mvp',
    sport: 'general',
    category: 'mvp',
    name: { ru: '10× MVP', en: '10× MVP' },
    description: { ru: '10 раз MVP игры', en: 'MVP 10 times' },
    icon: 'crown',
    color: '#FBBF24',
    check: (s) => s.mvpCount >= 10,
    progress: (s) => ({ current: Math.min(s.mvpCount, 10), target: 10 }),
  },
  {
    key: 'general.elo_2100',
    sport: 'general',
    category: 'rating',
    name: { ru: 'Любитель', en: 'Amateur' },
    description: { ru: 'Рейтинг ≥ 2100', en: 'Rating ≥ 2100' },
    icon: 'star',
    color: '#3B82F6',
    check: (s) => s.eloMax >= 2100,
    progress: (s) => ({ current: Math.min(s.eloMax, 2100), target: 2100 }),
  },
  {
    key: 'general.elo_2250',
    sport: 'general',
    category: 'rating',
    name: { ru: 'Полупро', en: 'Semi-pro' },
    description: { ru: 'Рейтинг ≥ 2250', en: 'Rating ≥ 2250' },
    icon: 'star',
    color: '#A855F7',
    check: (s) => s.eloMax >= 2250,
    progress: (s) => ({ current: Math.min(s.eloMax, 2250), target: 2250 }),
  },
  {
    key: 'general.elo_2400',
    sport: 'general',
    category: 'rating',
    name: { ru: 'Зверь', en: 'Beast' },
    description: { ru: 'Рейтинг ≥ 2400', en: 'Rating ≥ 2400' },
    icon: 'star',
    color: '#FF5C35',
    check: (s) => s.eloMax >= 2400,
    progress: (s) => ({ current: Math.min(s.eloMax, 2400), target: 2400 }),
  },
  {
    key: 'general.three_sports',
    sport: 'general',
    category: 'social',
    name: { ru: 'Триатлет', en: 'Triathlete' },
    description: { ru: 'Игры в 3 разных видах спорта', en: 'Played 3 different sports' },
    icon: 'target',
    color: '#22C55E',
    check: (s) => s.sportsPlayed >= 3,
    progress: (s) => ({ current: Math.min(s.sportsPlayed, 3), target: 3 }),
  },
  {
    key: 'general.five_sports',
    sport: 'general',
    category: 'social',
    name: { ru: 'Пятиборец', en: 'Pentathlete' },
    description: { ru: 'Игры в 5 разных видах спорта', en: 'Played 5 different sports' },
    icon: 'target',
    color: '#A855F7',
    check: (s) => s.sportsPlayed >= 5,
    progress: (s) => ({ current: Math.min(s.sportsPlayed, 5), target: 5 }),
  },
  {
    key: 'general.three_clubs',
    sport: 'general',
    category: 'social',
    name: { ru: 'Многоклубный', en: 'Multi-club' },
    description: { ru: 'Участие в 3 клубах', en: 'Played in 3 different clubs' },
    icon: 'users',
    color: '#3B82F6',
    check: (s) => s.clubsPlayed >= 3,
    progress: (s) => ({ current: Math.min(s.clubsPlayed, 3), target: 3 }),
  },
  {
    key: 'general.fifty_partners',
    sport: 'general',
    category: 'social',
    name: { ru: 'Свой парень', en: 'Well-known' },
    description: { ru: 'Сыграл с 50 разными игроками', en: 'Played with 50 different people' },
    icon: 'users',
    color: '#A855F7',
    check: (s) => s.partnersUnique >= 50,
    progress: (s) => ({ current: Math.min(s.partnersUnique, 50), target: 50 }),
  },
  {
    key: 'general.organizer_5',
    sport: 'general',
    category: 'social',
    name: { ru: 'Организатор', en: 'Organizer' },
    description: { ru: 'Создал 5 игр', en: 'Organized 5 games' },
    icon: 'award',
    color: '#06B6D4',
    check: (s) => s.organizedGames >= 5,
    progress: (s) => ({ current: Math.min(s.organizedGames, 5), target: 5 }),
  },
  {
    key: 'general.organizer_25',
    sport: 'general',
    category: 'social',
    name: { ru: 'Капитан клуба', en: 'Club captain' },
    description: { ru: 'Создал 25 игр', en: 'Organized 25 games' },
    icon: 'award',
    color: '#FF5C35',
    check: (s) => s.organizedGames >= 25,
    progress: (s) => ({ current: Math.min(s.organizedGames, 25), target: 25 }),
  },
];
